import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

/**
 * Reveal children when they scroll into view.
 *  - Once triggered, stays visible (no re-fade on scroll out).
 *  - Respects `prefers-reduced-motion` (shows instantly).
 *
 * Props:
 *   delay     — ms to wait after entering view (default 0)
 *   duration  — animation duration in ms (default 600)
 *   y         — initial translateY in px (default 24)
 *   x         — initial translateX in px (default 0)
 *   threshold — IntersectionObserver threshold (default 0.15)
 *   once      — set false to re-animate on every entry (default true)
 *   as        — Box `component` override (default "div")
 *   sx        — extra MUI sx
 */
const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const RevealOnScroll = ({
  children,
  delay = 0,
  duration = 600,
  y = 24,
  x = 0,
  threshold = 0.15,
  once = true,
  enabled = true,
  as = "div",
  sx = {},
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) return;
    if (!enabled) return; // hold off until parent signals ready
    const node = ref.current;
    if (!node) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, once, enabled]);

  return (
    <Box
      ref={ref}
      component={as}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate3d(0,0,0)" : `translate3d(${x}px, ${y}px, 0)`,
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: "opacity, transform",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default RevealOnScroll;
